/**
 * Ontology glyph adapter — the KEY mapping between the real Cade node `type`
 * (see `CATEGORY_MAP` in categoryColors.ts) and the OBSIDIAN graph's five
 * ontological node classes (graph-spec.md §1).
 *
 * Faithful port of obgraph.js `gtxClass` + `GTX_TYPE` + `OB_TYPE_ICON`, widened
 * to cover the full real-world type set rather than the prototype's demo subset.
 *
 *   glyphClass  → which glyph builder draws the node:
 *     'boundary'  → plain large globe (internet)
 *     'activity'  → octagon (alert / incident)
 *     'geo'       → muted map-pin property node (geolocation)
 *     'mitre'     → muted hexagon property node (mitre_technique)
 *     'entity'    → default collar circle (everything else)
 *   collarType   → the short TYPE label that rides the node's circumference.
 *   isProperty   → geolocation / mitre_technique — muted, dotted-linked.
 */

import { getCategoryForType, CATEGORY_COLORS, type Category } from '../categoryColors';

export type GlyphClass = 'entity' | 'activity' | 'boundary' | 'geo' | 'mitre';

export interface OntologyGlyph {
  glyphClass: GlyphClass;
  /** TYPE label shown on the collar / eyebrow (e.g. USER, HOST, IP, SSO, DATA). */
  collarType: string;
  /** geolocation / mitre_technique — muted "property" nodes on dotted links. */
  isProperty: boolean;
}

/** Lucide icon name per node type (graph-spec.md §5). Superset of obgraph.js OB_TYPE_ICON. */
export const TYPE_ICON: Record<string, string> = {
  // Network
  internet: 'globe',
  ip: 'radio-tower',
  url: 'link',
  domain: 'globe',
  network_zone: 'globe',
  asn: 'radio-tower',
  security_group: 'globe',
  // Application
  okta: 'app-window',
  application: 'app-window',
  email: 'app-window',
  cloud_resource: 'app-window',
  api_endpoint: 'app-window',
  container_workload: 'app-window',
  // Identity
  user: 'user',
  group: 'users',
  iam_role: 'user',
  service_account: 'user',
  certificate: 'user',
  // 'secret' is an ontology entity TYPE (a stored-secret resource node, e.g. a
  // Secrets-Manager secret), mapped to its glyph icon — not a hardcoded credential.
  secret: 'user', // nosemgrep
  api_key_token: 'user',
  // Compute
  host: 'monitor',
  ec2: 'server',
  lambda: 'server',
  process: 'server',
  container: 'server',
  k8s_pod: 'server',
  k8s_node: 'server',
  eks_cluster: 'server',
  aks_cluster: 'server',
  gke_cluster: 'server',
  auto_scaling_group: 'server',
  // Findings
  alert: 'triangle-alert',
  incident: 'triangle-alert',
  vulnerability: 'triangle-alert',
  threat_actor: 'triangle-alert',
  campaign: 'triangle-alert',
  mitre: 'crosshair',
  mitre_technique: 'crosshair',
  indicator_ioc: 'triangle-alert',
  // Data
  s3: 'archive',
  s3_bucket: 'archive',
  storage_bucket: 'archive',
  database: 'database',
  rds: 'database',
  dynamodb: 'database',
  file: 'archive',
  data_finding: 'archive',
  data_store: 'database',
  // Context
  geo: 'map-pin',
  geolocation: 'map-pin',
  organization: 'users',
  // Framework
  policy: 'app-window',
};

/**
 * Short TYPE label that rides the collar (graph-spec.md §6). Superset of
 * obgraph.js GTX_TYPE covering the full real type set; fallback (handled in
 * `ontologyGlyph`) = the category display name uppercased.
 */
export const TYPE_LABEL: Record<string, string> = {
  // Network
  internet: 'NET',
  ip: 'IP',
  url: 'URL',
  domain: 'DOMAIN',
  network_zone: 'ZONE',
  asn: 'ASN',
  security_group: 'SG',
  // Application
  okta: 'SSO',
  application: 'APP',
  email: 'EMAIL',
  cloud_resource: 'CLOUD',
  api_endpoint: 'API',
  container_workload: 'WORKLOAD',
  // Identity
  user: 'USER',
  group: 'GROUP',
  iam_role: 'ROLE',
  service_account: 'SVC',
  certificate: 'CERT',
  // 'secret' is an ontology entity TYPE (a stored-secret resource node), mapped
  // to its collar display label — not a hardcoded credential.
  secret: 'SECRET', // nosemgrep
  api_key_token: 'TOKEN',
  // Compute
  host: 'HOST',
  ec2: 'EC2',
  lambda: 'LAMBDA',
  process: 'PROC',
  container: 'CONTAINER',
  k8s_pod: 'POD',
  k8s_node: 'NODE',
  eks_cluster: 'EKS',
  aks_cluster: 'AKS',
  gke_cluster: 'GKE',
  auto_scaling_group: 'ASG',
  // Findings
  alert: 'ALERT',
  incident: 'INCIDENT',
  vulnerability: 'VULN',
  threat_actor: 'ACTOR',
  campaign: 'CAMPAIGN',
  mitre: 'MITRE',
  mitre_technique: 'MITRE',
  indicator_ioc: 'IOC',
  // Data
  s3: 'DATA',
  s3_bucket: 'DATA',
  storage_bucket: 'DATA',
  database: 'DB',
  rds: 'RDS',
  dynamodb: 'DDB',
  file: 'FILE',
  data_finding: 'DATA',
  data_store: 'STORE',
  // Context
  geo: 'LOCATION',
  geolocation: 'LOCATION',
  organization: 'ORG',
  // Framework
  policy: 'POLICY',
};

/** Display name of a category, uppercased — the collar fallback. */
const CATEGORY_DISPLAY: Record<Category, string> = {
  identity: 'IDENTITY',
  compute: 'COMPUTE',
  network: 'NETWORK',
  data: 'DATA',
  findings: 'FINDINGS',
  application: 'APP',
  context: 'CONTEXT',
  framework: 'POLICY',
};

const PROPERTY_TYPES = new Set(['geolocation', 'mitre_technique', 'geo', 'mitre']);

function glyphClassFor(type: string): GlyphClass {
  if (type === 'internet') return 'boundary';
  if (type === 'alert' || type === 'incident') return 'activity';
  if (type === 'geolocation' || type === 'geo') return 'geo';
  if (type === 'mitre_technique' || type === 'mitre') return 'mitre';
  return 'entity';
}

/**
 * Map a real Cade node type to its OBSIDIAN glyph class, collar TYPE label and
 * property flag. `type` is whatever the element `data.type` carries.
 */
export function ontologyGlyph(type: string | undefined | null): OntologyGlyph {
  const t = (type ?? 'unknown').toString();
  const glyphClass = glyphClassFor(t);
  const collarType = TYPE_LABEL[t] ?? CATEGORY_DISPLAY[getCategoryForType(t)];
  return {
    glyphClass,
    collarType,
    isProperty: PROPERTY_TYPES.has(t),
  };
}

/** Lucide icon name for a node type, falling back to the category default. */
export function iconNameFor(type: string | undefined | null): string {
  const t = (type ?? 'unknown').toString();
  if (TYPE_ICON[t]) return TYPE_ICON[t];
  const cat = getCategoryForType(t);
  return CATEGORY_DEFAULT_ICON[cat];
}

/** Default Lucide icon per category (graph-spec.md §3 "default icon" column). */
const CATEGORY_DEFAULT_ICON: Record<Category, string> = {
  identity: 'users',
  compute: 'server',
  network: 'globe',
  data: 'database',
  findings: 'triangle-alert',
  application: 'app-window',
  context: 'map-pin',
  framework: 'app-window',
};

/**
 * Resolve fill / stroke / edge colors for a category at a given theme. Thin
 * wrapper over CATEGORY_COLORS so the renderer can grab all three at once
 * (the obgraph.js `obCat` helper).
 */
export function categoryColors(type: string | undefined | null, dark: boolean): {
  fill: string;
  stroke: string;
  edge: string;
} {
  const cat = getCategoryForType(type ?? 'unknown');
  const c = CATEGORY_COLORS[cat];
  return {
    fill: dark ? c.fill.dark : c.fill.light,
    stroke: dark ? c.stroke.dark : c.stroke.light,
    edge: dark ? c.edge.dark : c.edge.light,
  };
}
